<template>
  <v-container class="archive pa-0 ma-0" fluid>
    <v-card>
      <v-card-title>
        <v-toolbar flat style="cursor: default;">
          <v-toolbar-title class="ml-2 title"><v-icon left>mdi-file-upload-outline</v-icon>
            {{ $route.name }} Files
          </v-toolbar-title>
          <v-divider class="mx-4" inset vertical></v-divider>
          <v-spacer></v-spacer>
          <v-col class="d-flex justify-end">
            <v-btn
              id="uploadBtn"
              color="success"
              class="mr-4"
              :loading="loading"
              :disabled="loading"
              @click="uploadAgain = false, validateUpload()">
              <v-icon>mdi-file-upload-outline</v-icon>
              Save
            </v-btn>
            <v-btn
              v-if="['jbg', 'alliance-retail-group'].includes($auth.tenant) && !isEditMode"
              id="saveAndUploadNewBtn"
              color="success"
              class="mr-4"
              :loading="loading"
              :disabled="loading"
              @click="uploadAgain = true, validateUpload()">
              <v-icon>mdi-file-upload-outline</v-icon>
              Save and Upload New
            </v-btn>
            <v-btn
              color="warning"
              @click="reset">
              Reset
            </v-btn>
          </v-col>
        </v-toolbar>
      </v-card-title>
      <v-card-text>
        <v-container fluid>
          <v-form ref="form" lazy-validation v-model="valid">
            <v-row dense>
              <v-col>
                <v-text-field
                  label="Name"
                  v-model="doc.name"
                  dense
                  required
                  :rules="[v => !!v || 'Name is required']"
                  validate-on-blur
                  outlined
                  hide-details="auto">
                </v-text-field>
              </v-col>
              <v-col>
                <v-text-field
                  label="Description"
                  v-model="doc.description"
                  dense
                  outlined
                  hide-details="auto">
                </v-text-field>
              </v-col>
            </v-row>
            <DocumentTags
              v-model="doc.tags">
            </DocumentTags>
            <v-row>
              <v-col>
                <v-file-input
                  v-model="files"
                  outlined
                  dense
                  multiple
                  hide-details="auto"
                  truncate-length="100"
                  label="Files"
                  @change="handleFiles">
                </v-file-input>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <DocPartyTable
                  v-model="docPartyIds"
                  :docId="doc.id || null">
                </DocPartyTable>
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>
    </v-card>
    <ConfirmFileOverwriteModal
      v-if="modal"
      :value.sync="modal"
      :documentFileName="documentFileName"
      :partiesGap="partiesGap"
      @close="closeModal"
      @reject="closeModal"
      @confirm="saveDocument(true)"
    />
  </v-container>
</template>
<script lang="js">
// api
import document from '@/axios/document.ts'
import party from '@/axios/doc-party.ts'
import docfile from '@/axios/file.ts'

// components
import DocPartyTable from '@/components/DocPartyTable.vue'
import DocumentTags from '@/components/DocumentTags.vue'
import ConfirmFileOverwriteModal from '@/components/ConfirmFileOverwriteModal.vue'

// mixins
import { displayAlert } from '@/mixins/alert'
import { partySearch } from '@/mixins/party-search'

export default {
  name: 'upload',
  data () {
    return {
      doc: {
        root_key: '',
        name: '',
        description: '',
        metadata: {},
        tags: {
          category: '',
          subcategory: '',
          status: 'CURRENT'
        }
      },
      docParties: [],
      partiesGap: [],
      docPartyIds: [],
      docMeta: [],
      files: [],
      metaheaders: [
        { text: 'Key', value: 'key', sortable: false, filterable: false, divider: true },
        { text: 'Value', value: 'value', sortable: false, filterable: false },
        { text: '', value: 'actions', sortable: false, filterable: false }
      ],
      newFile: false,
      newParty: false,
      valid: true,
      loading: false,
      modal: false,
      uploadAgain: false,
      documentFileName: ''
    }
  },
  components: { DocPartyTable, DocumentTags, ConfirmFileOverwriteModal },
  mixins: [displayAlert, partySearch],
  watch: {
    $route: {
      handler (newValue, oldValue) {
        if (newValue) {
          if (newValue.query?.id) {
            this.initDocData()
          } else {
            if (oldValue.query.id) {
              this.resetDocData()
            }
          }
        }
      },
      deep: true
    }
  },
  async created () {
    this.doc.root_key = this.$store.getters.root_key
    if (this.$route.query?.id) {
      await this.initDocData()
    } else {
      // Handle category/subcategory from file explorer
      if (this.$route.query?.category) {
        this.doc.tags.category = this.$route.query.category
      }
      if (this.$route.query?.subcategory) {
        this.doc.tags.subcategory = this.$route.query.subcategory
      }
      if (this.$route.query?.subtype) {
        this.doc.tags.subtype = this.$route.query.subtype
      }
    }
  },
  computed: {
    id () {
      return this.$route.query.id || null
    },
    userData () {
      return this.$store.getters.userData
    },
    isEditMode () {
      return Boolean(this.$route.query?.id)
    }

  },
  methods: {
    async initDocData () {
      try {
        const res = await document.get(this.id)
        this.doc = res.data
        if (this.doc.document_file) {
          this.files = [this.doc.document_file]
        }
        if (this.doc.metadata) {
          this.docMeta = Object.keys(this.doc.metadata).map(key => {
            const metaObj = {
              key: key,
              value: this.doc.metadata[key]
            }
            return metaObj
          })
        }
        if (this.doc.parties?.length > 0) {
          const ids = this.doc.parties.map(party => party.party_id)
          this.docPartyIds = JSON.parse(JSON.stringify(ids))
        }
      } catch (err) {
        console.error(err)
        this.emitAlert(true, 'warning', err.message)
      }
    },
    handleFiles (files) {
      const reader = new FileReader()
      for (const [i, file] of files.entries()) {
        reader.onload = (e) => {
          this.files[i].upload_file = e.target.result
        }
        reader.readAsArrayBuffer(file)
      }
    },
    reset () {
      this.$refs.form.reset()
    }, 
    calcPartyGap (existingParties, newPartyIds) {
      return existingParties.filter(p => !newPartyIds.includes(p.party_id))
    }, 
    async validateUpload () {
      try {
        const isValid = this.$refs.form.validate()
        if (isValid !== true) {
          return
        }
        if (this.docPartyIds.length === 0) {
          return this.emitAlert(true, 'warning', 'Please add a party')
        }
        this.loading = true
        if (this.docMeta.length > 0) {
          const entries = this.docMeta.map(obj => {
            return [`${obj.key}`, `${obj.value}`]
          })
          this.doc.metadata = Object.fromEntries(entries)
        }
        if (this.isEditMode) {
          await this.saveDocument(true)
          this.loading = false
        } else {
          this.documentFileName = this.files[0].name
          const document_tags = Object.fromEntries(Object.entries(this.doc.tags), ([k, v]) => [k, v])
          const data = { document_file_name: this.documentFileName }
          const res = await document.search(data, document_tags, 0, 10)

          if (res?.data.length > 0) {
            this.doc.id = res.data[0].id
            this.doc.parties = res.data[0].parties

            const partyGapIds = this.calcPartyGap(res.data[0].parties, this.docPartyIds)
            const term = partyGapIds.map((party) => party.party_id).join(' ')
            if (term) this.partiesGap = await this.queryParties(term, 'or')
            else this.partiesGap = []

            this.modal = true
          } else {
            await this.createDoc()
            await this.saveDocument(false)
          }
        }
      } catch (err) {
        this.loading = false
        console.error(err)
        this.emitAlert(true, 'warning', err.message)
      }
    },
    async saveDocument (shouldUpdate) {
      try {
        this.modal = false
        if (!shouldUpdate && this.files.length > 0 && this.files[0].upload_file) {
          await this.uploadFile()
        } else if (shouldUpdate && this.files.length > 0) {
          await this.updateDoc(this.doc)
          await this.uploadFile()
        }
        await this.handleDocParties()
        if (this.doc.tags.status === 'CURRENT') {
          // Commenting out this line to prevent updating a current document from archiving other current documents
          //    Documents with at least 1 of the same parties and the same category and subcategory are what is looked for in this comparison
          // await this.updateLatest()
        }
        if (!this.isEditMode) {
          this.loading = false
          this.emitAlert(true, 'success', 'Saved!')
          this.$gtag.event('document_upload', {
            category: 'documents',
            document_category: this.doc.tags?.category || '',
            document_subcategory: this.doc.tags?.subcategory || '',
            file_count: this.files.length
          })
          if (this.uploadAgain) {
            this.resetDocData()
            return
          } else {
            return this.$router.push({
              name: 'Edit',
              query: { id: this.doc.id }
            })
          }
        } else {
          this.loading = false
          this.emitAlert(true, 'success', 'Saved!')
          return this.initDocData()
        }
      } catch (err) {
        this.loading = false
        console.error(err)
        this.emitAlert(true, 'warning', err.message)
      }
    },
    async createDoc () {
      const res = await document.post(this.doc)
      if (res?.data?.id) {
        this.doc.id = res.data.id
      }
      return res
    },
    async updateDoc (doc) {
      if (!doc.root_key) doc.root_key = this.$store.getters.root_key
      return await document.put(doc.id, doc)
    },
    async updateLatest () {
      try {
        // getting updated document data for search
        const doc = await document.get(this.doc.id)
        const tags = doc.data.tags
        const data = { party_ids: doc.data.parties.map(p => p.party_id) }

        const res = await document.search(data, tags, 0, 200)
        const docsToUpdate = res.data.filter(d => d.id !== this.doc.id)

        if (docsToUpdate.length > 0) {
          const res = await Promise.allSettled(docsToUpdate.map(async d => {
            d.tags.status = 'ARCHIVE'
            return await this.updateDoc(d)
          }))
          const rejected = res.flatMap(result => {
            return result.status === 'rejected'
              ? result.reason
              : []
          })
          if (rejected.length > 0) throw rejected
          else return res
        }
        return
      } catch (err) {
        console.error(err)
        throw err
      }
    },
    async uploadFile () {
      try {
        const file = this.files[0]
        const blob = new Blob([file.upload_file], { type: `${file.type}` })
        const { category, subcategory, subtype } = this.doc.tags
        const formData = new FormData()

        const pathEnv = `${process.env.VUE_APP_FILE_PATH}`
        let path = `${pathEnv}/${category}/${subcategory}/`
        if (subtype) {
          path = `${path}/${subtype}/`
        }
        formData.append('path', path)
        formData.append('name', `${file.name}`)
        formData.append('upload_file', blob, `${file.name}`)

        return await docfile.put(this.doc.id, formData)
      } catch (err) {
        console.error(err)
        throw err
      }
    },
    async handleDocParties () {
      const partiesToAdd = (this.docPartyIds.length > 0)
        ? this.docPartyIds.filter(id => {
          if (!this.doc.parties || this.doc.parties?.length === 0) {
            return id
          }
          const matches = this.doc.parties.filter(p => p.party_id === id)
          if (matches.length === 0) {
            return id
          }
        })
        : []
      const partiesToDelete = (this.doc?.parties?.length > 0)
        ? this.doc.parties.flatMap(party => {
          return (!this.docPartyIds.includes(party.party_id))
            ? party.party_id
            : []
        })
        : []
      return await Promise.allSettled([
        this.updateDocParties(partiesToAdd, 'post'),
        this.updateDocParties(partiesToDelete, 'delete')
      ]).catch(err => {
        throw err
      })
    },
    async updateDocParties (partyIds, action) {
      if (partyIds.length === 0) return []
      const results = await Promise.allSettled(partyIds.map(async id => {
        if (action === 'post') {
          return await party.post(this.doc.id, { party_id: id })
        } else if (action === 'delete') {
          return await party.delete(this.doc.id, id)
        }
      }))
      const rejected = results.flatMap(result => {
        return result.status === 'rejected'
          ? result.reason
          : []
      })
      if (rejected.length > 0) throw rejected
      else return results
    },
    resetDocData () {
      this.doc = {
        root_key: this.$store.getters.root_key,
        name: '',
        description: '',
        metadata: {},
        tags: {
          category: '',
          subcategory: '',
          status: 'CURRENT'
        }
      }
      this.docPartyIds = []
      this.partiesGap = []
      this.docMeta = []
      this.files = []
    },
    closeModal () {
      this.modal = false
      this.loading = false
    }
  }
}
</script>